/**
 * Onboarding Zustand store — FE-W-02b.
 *
 * 정책:
 *   - server response 가 currentStep / domain / institution 의 단일 진실원
 *     (pattern-store-overlay-with-sse-pushed-authority — overlay 우선)
 *   - sessionId / token / password / OTP 는 store 에 절대 보관 0 (XSS surface 최소화)
 *   - persist middleware 미사용 — XSS 시 localStorage 토큰 누출 차단 (Auditor A-4 + ADR-1535)
 *   - 모든 mutation 은 server response 받은 후에만 — 클라이언트 임의 step 점프 금지
 *
 * 흐름:
 *   1. restore() — 마운트 시 GET /session 호출. 204 → 초기 상태. 410 → store.reset + expired navigate
 *   2. start({ companyName }) — POST /start. 응답으로 currentStep=1
 *   3. setDomainAction({ domain }) — POST /domain. 응답으로 currentStep=2 + dnsChallenge 보관 (Step3 안내)
 *   4. verifyAction({ method, otp? }) — POST /verify. 응답 분기: success | otp_pending
 *   5. finalizeAction({ email, password, displayName }) — POST /admin. 성공 시 store.reset() + authStore.setSession
 *   6. reset() — finalize 완료 또는 race/expired 시 초기 상태
 */

import { create } from 'zustand';
import {
  startOnboarding,
  setDomain,
  verifyDomain,
  finalize,
  restoreSession,
  OnboardingError,
  type StartResponse,
  type SetDomainResponse,
  type VerifyMethod,
  type VerifySuccessResponse,
  type VerifyOtpPendingResponse,
  type FinalizeResponse,
} from '../api/onboarding';

export type OnboardingStep = 0 | 1 | 2 | 3 | 4;

export interface OnboardingState {
  currentStep: OnboardingStep;
  companyName: string | null;
  domain: string | null;
  institutionId: string | null;
  verificationMethod: 'txt' | 'mx_otp' | null;
  /** Step 2 응답의 TXT record value — Step3 가 안내 메시지에 사용. 새로고침 시 잃을 수 있음 (server 가 단일 노출). */
  dnsChallengeValue: string | null;
  /** dev SMTP bypass 모드에서만 노출되는 OTP — production 에서는 항상 null. */
  devBypassOtp: string | null;
  loading: boolean;
  error: { code: string; message: string; retryable?: boolean } | null;
  /** Step 4 finalize 후 grace_until — AdminBanner 가 표시. */
  graceUntil: string | null;

  // ----- actions -----
  restore: () => Promise<{ status: 'restored' | 'no_session' | 'expired' }>;
  start: (payload: { companyName: string }) => Promise<void>;
  setDomainAction: (payload: { domain: string }) => Promise<void>;
  verifyAction: (
    payload: { method: VerifyMethod; otp?: string },
  ) => Promise<VerifySuccessResponse | VerifyOtpPendingResponse>;
  finalizeAction: (payload: {
    email: string;
    password: string;
    displayName?: string;
  }) => Promise<FinalizeResponse>;
  reset: () => void;
  clearError: () => void;
}

const initialState = {
  currentStep: 0 as OnboardingStep,
  companyName: null,
  domain: null,
  institutionId: null,
  verificationMethod: null,
  dnsChallengeValue: null,
  devBypassOtp: null,
  loading: false,
  error: null,
  graceUntil: null,
} satisfies Omit<
  OnboardingState,
  'restore' | 'start' | 'setDomainAction' | 'verifyAction' | 'finalizeAction' | 'reset' | 'clearError'
>;

function toErrorPayload(err: unknown): { code: string; message: string; retryable?: boolean } {
  if (err instanceof OnboardingError) {
    return { code: err.payload.code, message: err.payload.message, retryable: err.payload.retryable };
  }
  if (err instanceof Error) return { code: 'NETWORK_ERROR', message: err.message };
  return { code: 'UNKNOWN_ERROR', message: 'Unknown error' };
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  ...initialState,

  restore: async () => {
    set({ loading: true, error: null });
    try {
      const r = await restoreSession();
      if (r === null) {
        // 204 — cookie 없음, 초기 상태 유지
        set({ ...initialState });
        return { status: 'no_session' };
      }
      set({
        currentStep: r.currentStep,
        companyName: r.companyName,
        domain: r.domain,
        institutionId: r.institutionId,
        verificationMethod: r.verificationMethod,
        loading: false,
        error: null,
      });
      return { status: 'restored' };
    } catch (err) {
      if (err instanceof OnboardingError && err.payload.code === 'SESSION_EXPIRED') {
        set({ ...initialState, error: toErrorPayload(err) });
        return { status: 'expired' };
      }
      set({ loading: false, error: toErrorPayload(err) });
      return { status: 'no_session' };
    }
  },

  start: async (payload) => {
    set({ loading: true, error: null });
    try {
      const r: StartResponse = await startOnboarding(payload);
      set({
        currentStep: r.currentStep,
        companyName: r.companyName,
        domain: r.domain,
        loading: false,
      });
    } catch (err) {
      set({ loading: false, error: toErrorPayload(err) });
      throw err;
    }
  },

  setDomainAction: async (payload) => {
    set({ loading: true, error: null });
    try {
      const r: SetDomainResponse = await setDomain(payload);
      set({
        currentStep: r.currentStep,
        domain: r.institution.domain,
        institutionId: r.institution.id,
        dnsChallengeValue: r.dnsChallenge.recordValue,
        loading: false,
      });
    } catch (err) {
      set({ loading: false, error: toErrorPayload(err) });
      throw err;
    }
  },

  verifyAction: async (payload) => {
    set({ loading: true, error: null });
    try {
      const r = await verifyDomain(payload);
      if ('status' in r && r.status === 'otp_sent') {
        set({
          verificationMethod: 'mx_otp',
          devBypassOtp: r.devBypassOtp ?? null,
          loading: false,
        });
        return r;
      }
      // success
      set({
        currentStep: 3,
        verificationMethod: r.method === 'dns_txt' ? 'txt' : 'mx_otp',
        loading: false,
      });
      return r;
    } catch (err) {
      set({ loading: false, error: toErrorPayload(err) });
      throw err;
    }
  },

  finalizeAction: async (payload) => {
    set({ loading: true, error: null });
    try {
      const r: FinalizeResponse = await finalize(payload);
      // Cycle 72 — finalize 는 승인 대기(토큰/grace 없음). grace 는 승인 후 시작 → graceUntil 미설정.
      set({
        currentStep: 4,
        loading: false,
      });
      return r;
    } catch (err) {
      set({ loading: false, error: toErrorPayload(err) });
      throw err;
    }
  },

  // finalize 후 reset 호출 시 graceUntil 은 보존 — AdminBanner 가 표시 (FE-W-11).
  // 새로고침 시 graceUntil 잃음 (alone-tab 한정) — 다음 cycle 에서 /users/me 의 institution metadata 로 영속.
  reset: () => {
    const preserveGrace = get().graceUntil;
    set({ ...initialState, graceUntil: preserveGrace });
  },
  clearError: () => set({ error: null }),
}));

// Test 전용 — store reset (각 test isolation)
export function _resetOnboardingStoreForTests(): void {
  useOnboardingStore.setState({ ...initialState });
}
